import React, { FC } from 'react'

import { GooglePlaceSection, Grid } from '@mytours/components'
import { captureApiException, isNetworkError } from 'mobile_web/modules/api/errors'
import { useOnline } from 'mobile_web/modules/offline/useOnline'
import { getDirMode } from 'mobile_web/modules/platform'
import strings from 'mobile_web/strings'
import { getDirectApi, ScreenSection } from 'mytours-read-common'

import SectionHeader from './SectionHeader'
import '../styles/sectionGooglePlace.less'

const SESSION_KEY_STORAGE_KEY = 'google_place_session_key'

let sessionKey: string | undefined

// crypto.randomUUID is missing before Safari 15.4, which Vite 5's default build target still supports.
const newSessionKey = (): string =>
	typeof crypto.randomUUID === 'function'
		? crypto.randomUUID()
		: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`

// Core bills a render once per section per key, so the key has to outlive a reload of the tab.
const renderSessionKey = (): string => {
	if (sessionKey) return sessionKey
	try {
		sessionKey = sessionStorage.getItem(SESSION_KEY_STORAGE_KEY) ?? undefined
		if (!sessionKey) {
			sessionKey = newSessionKey()
			sessionStorage.setItem(SESSION_KEY_STORAGE_KEY, sessionKey)
		}
	} catch {
		// Storage can be blocked outright; a key for this page load still stops re-renders billing.
		sessionKey ??= newSessionKey()
	}
	return sessionKey
}

interface Props {
	section: ScreenSection
}

const SectionGooglePlace: FC<Props> = ({ section }) => {
	const online = useOnline()

	const reportRender = () => {
		if (section.id === undefined) return
		getDirectApi()
			.storySectionRenderCreate(section.id, { session_key: renderSessionKey() })
			.catch(error => {
				if (isNetworkError(error)) return
				captureApiException(error, 'storySectionRenderCreate')
			})
	}

	return (
		<Grid className="section-google-place" dir={getDirMode()}>
			{!!section.title && <SectionHeader title={section.title} />}
			<GooglePlaceSection
				className={`block-content ${getDirMode()}`}
				dir={getDirMode()}
				dataCy="story-screen-section"
				itemId={section.id}
				name={section.fallback_name}
				address={section.fallback_address}
				openingHours={section.fallback_opening_hours}
				website={section.fallback_website}
				phone={section.fallback_phone}
				live={online}
				placeId={section.place_id}
				apiKey={ENV.GOOGLE_PLACES_UI_KIT_API_KEY}
				onLiveLoad={reportRender}
				collapsable={section.collapsable}
				exposableContent={section.exposable_content}
				expandLabel={strings.expand}
				collapseLabel={strings.collapse}
			/>
		</Grid>
	)
}

export default SectionGooglePlace
